import { useEffect, useState } from "react";
import { api } from "../api";
import { useLoad } from "../hooks";
import { CommandBar } from "../Shell";
import { bcTarget, bcUrl, config, saveBcTarget } from "../config";

const MCP_URL = "https://mcp.couragemfg.com/mcp";
const CLAUDE_CLIENT_ID = "35adf40b-1a17-4d4c-81c8-900b2ddd1ca0";

function Row({ name, status, tone, children }: { name: string; status: string; tone: string; children: React.ReactNode }) {
  return (
    <section className="card">
      <div className="card-head"><h2>{name}</h2><span className={`status ${tone}`}>{status}</span></div>
      <div className="card-body">{children}</div>
    </section>
  );
}

export function IntegrationsPage({ name }: { name: string }) {
  const health = useLoad(() => api.health());
  const me = useLoad(() => api.me());
  const stats = useLoad(() => api.ontologyStats());
  const bcs = useLoad(() => api.bcStatus());
  const st = bcs.data;
  const ols = useLoad(() => api.outlookStatus());
  const ol = ols.data;
  const olLabel = !ol ? (ols.error ? "Can't connect" : "Checking…") : ol.ok ? "Connected" : ol.code === "consent_required" ? "Needs admin consent" : ol.configured ? "Can't connect" : "Not connected";
  const olTone = ol?.ok ? "ok" : ol?.configured ? "danger" : "warn";
  const bcTone = st?.ok ? "ok" : st?.configured ? "danger" : "warn";
  // BC links follow the connected environment unless this browser chose another.
  useEffect(() => {
    try { if (st?.ok && st.environment && !localStorage.getItem("bc.environment")) { saveBcTarget({ environment: st.environment }); setBc(bcTarget()); } } catch {}
  }, [st?.ok, st?.environment]);
  const bcLabel = !st ? "Checking…" : st.ok ? "Connected" : st.code === "consent_required" ? "Needs admin consent" : st.configured ? "Can't connect" : "Not connected";
  const [copied, setCopied] = useState("");
  const [bc, setBc] = useState(bcTarget);
  const [bcSaved, setBcSaved] = useState(false);
  const saveBc = (e: React.FormEvent) => { e.preventDefault(); saveBcTarget(bc); setBc(bcTarget()); setBcSaved(true); setTimeout(() => setBcSaved(false), 1500); };
  const copy = async (t: string) => { try { await navigator.clipboard.writeText(t); setCopied(t); setTimeout(() => setCopied(""), 1500); } catch {} };
  const linked = stats.data?.links.reduce((s, l) => s + l.distinctRecords, 0) ?? 0;

  return (
    <>
      <CommandBar status="Admin" />
      <div className="page">
        <div className="page-title"><h1>Integrations</h1><span className="sub">How courageMFG connects to Microsoft, its database, and AI assistants.</span></div>
        <div className="grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))" }}>
          <Row name="Microsoft Entra ID" status={me.data ? "Connected" : me.error ? "Error" : "Checking…"} tone={me.data ? "ok" : me.error ? "danger" : ""}>
            <dl className="dl">
              <dt>Signed in as</dt><dd>{me.data?.name ?? name}{me.data?.email ? ` (${me.data.email})` : ""}</dd>
              <dt>Tenant</dt><dd className="mono">{config.tenantId}</dd>
              <dt>App</dt><dd>courageMFG API <span className="mono muted">{config.clientId}</span></dd>
              <dt>Scope</dt><dd className="mono">access_as_user</dd>
            </dl>
          </Row>
          <Row name="Database (Neon Postgres)" status={health.data?.ok ? "Healthy" : health.error ? "Unreachable" : "Checking…"} tone={health.data?.ok ? "ok" : health.error ? "danger" : ""}>
            <dl className="dl">
              <dt>Database</dt><dd className="mono">{health.data?.database ?? "—"}</dd>
              <dt>Server time</dt><dd>{health.data ? new Date(health.data.now).toLocaleString() : "—"}</dd>
              <dt>Audit trail</dt><dd>Every change recorded, append-only</dd>
            </dl>
          </Row>
          <Row name="Business Central" status={bcLabel} tone={st ? bcTone : ""}>
            {st?.ok ? (
              <dl className="dl" style={{ marginTop: 0 }}>
                <dt>Environment</dt><dd>{st.environment}</dd>
                <dt>Company</dt><dd>{st.company?.name}{st.companies && st.companies.length > 1 ? <span className="muted"> · {st.companies.length} companies</span> : null}</dd>
                <dt>Signed in as</dt><dd>You: courageMFG calls BC with your own BC permissions</dd>
                <dt>Live today</dt><dd>Accounts: search, link and import BC customers; create customers; Account 360</dd>
              </dl>
            ) : st?.configured ? (
              <p style={{ marginTop: 0 }}><b>{st.error}</b>{st.code === "consent_required" && <> A Microsoft Entra admin grants consent once for the organization: Entra admin center → App registrations → courageMFG API → API permissions → Grant admin consent.</>}</p>
            ) : (
              <p style={{ marginTop: 0 }}>Not connected yet. Once the courageMFG API has a client secret and a BC environment set, accounts read and create BC customers as each signed-in user.</p>
            )}
            <p className="muted" style={{ fontSize: 12 }}><b>{linked}</b> distinct BC records are linked to courageMFG records.</p>
            <p className="muted" style={{ fontSize: 12 }}><b>Warehouse app:</b> needs the Courage WMS extension installed in BC once (source in <span className="mono">bc/CourageWMS</span>), and the CMFG WMS permission set on warehouse users. It adds the API that posts bin moves and registers picks.</p>
            <form onSubmit={saveBc} style={{ marginBottom: 12 }}>
              <span className="label">Open Business Central pages in</span>
              <div className="btn-row" style={{ marginTop: 4 }}>
                <input className="input" style={{ flex: "1 1 120px" }} aria-label="BC environment" placeholder="Environment" value={bc.environment} onChange={(e) => setBc({ ...bc, environment: e.target.value })} />
                <input className="input" style={{ flex: "1 1 140px" }} aria-label="BC company" placeholder="Company (optional)" value={bc.company} onChange={(e) => setBc({ ...bc, company: e.target.value })} />
                <button className="btn">{bcSaved ? "Saved" : "Save"}</button>
                <a className="btn" href={bcUrl(22, bc)} target="_blank" rel="noopener noreferrer">Test</a>
              </div>
              <span className="hint">BC links in the navigation open here. Saved in this browser; the tenant is your sign-in tenant.</span>
            </form>
          </Row>
          <Row name="Outlook (Microsoft 365)" status={olLabel} tone={ol ? olTone : ""}>
            {ol?.ok ? (
              <dl className="dl" style={{ marginTop: 0 }}>
                <dt>Mailbox</dt><dd>{ol.mailbox}</dd>
                <dt>Signed in as</dt><dd>You: courageMFG reads and sends from your own mailbox and calendar</dd>
                <dt>Live today</dt><dd>Opportunities: mail and meetings with the buying committee, schedule Teams meetings; Quotes: send to the customer</dd>
              </dl>
            ) : ol?.configured ? (
              <p style={{ marginTop: 0 }}><b>{ol.error}</b>{ol.code === "consent_required" && <> Mail {ol.mail ? "✓" : "✗"} · Calendar {ol.calendar ? "✓" : "✗"}. Entra admin center → App registrations → courageMFG API → API permissions → add Mail.Read, Mail.Send, Calendars.ReadWrite → Grant admin consent.</>}</p>
            ) : (
              <p style={{ marginTop: 0 }}>Not connected yet. Uses the same courageMFG API client secret as Business Central.</p>
            )}
          </Row>
          <Row name="Claude and Microsoft Copilot (MCP)" status="Live" tone="ok">
            <p style={{ marginTop: 0 }}>Every courageMFG record and action is available to AI assistants as tools, with your permissions and full audit.</p>
            <div className="field"><span className="label">Connector URL</span>
              <div className="copy-row"><code>{MCP_URL}</code><button className="btn" onClick={() => copy(MCP_URL)}>{copied === MCP_URL ? "Copied" : "Copy"}</button></div></div>
            <div className="field" style={{ marginBottom: 0 }}><span className="label">Claude OAuth client ID</span>
              <div className="copy-row"><code>{CLAUDE_CLIENT_ID}</code><button className="btn" onClick={() => copy(CLAUDE_CLIENT_ID)}>{copied === CLAUDE_CLIENT_ID ? "Copied" : "Copy"}</button></div>
              <span className="hint">In Claude: Settings → Connectors → Add custom connector → Use your own OAuth client.</span></div>
          </Row>
        </div>
      </div>
    </>
  );
}
